import React, {useEffect, useState} from "react";
import {Box, Fade, Grid, IconButton, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../../../repositories/CampaignRepository";
import {buildCampaignBody, campaignToForm, validateCampaignForm} from "../../../../project/campaigns/utils";
import {usePhoneNumbers} from "../../../../project/campaigns/hooks/usePhoneNumbers";
import {useAmiExtensions} from "../../../../project/campaigns/hooks/useAmiExtensions";
import {CampaignDetailsFieldsNew} from "./CampaignDetailsFieldsNew";
import {CampaignCallFieldsNew} from "./CampaignCallFieldsNew";
import {CampaignScheduleFieldsNew} from "./CampaignScheduleFieldsNew";
import {PhoneNumbersInputNew} from "./PhoneNumbersInputNew";
import {PhoneNumbersTableNew} from "./PhoneNumbersTableNew";
import {FieldLabel, FieldHelper, selectSx} from "./formFieldShared";
import {subProjectLabel} from "../../../../../utils/subProjects";

const addResultMessage = (result) => {
  const skipped = (result?.alreadyInCampaign || 0) + (result?.duplicatesInRequest || 0) + (result?.empty || 0);
  return `Added ${result?.added ?? 0} phone number(s)` + (skipped ? `, skipped ${skipped} already in the campaign` : "");
};

// Creates a campaign, or edits `campaign` when it's given. Registry projects pass `projectId` and
// `subProjects`: the form then asks for the sub project the campaign calls through
// (default `defaultSubProjectId`), whose flow provides the AMI extensions.
export const CampaignFormDialogNew = ({
                                     open, onClose, onSaved, campaign = null,
                                     projectId, subProjects, defaultSubProjectId,
                                   }) => {
  const isEdit = !!campaign;
  const toForm = () => {
    const values = campaignToForm(campaign);
    return !values.subProjectId
      ? {...values, subProjectId: defaultSubProjectId != null ? String(defaultSubProjectId) : ""}
      : values;
  };
  const [form, setForm] = useState(toForm);
  const [saving, setSaving] = useState(false);
  const numbers = usePhoneNumbers();
  const subProject = subProjects.find(item => String(item.id) === String(form.subProjectId));
  // the selected sub project's extensions
  const amiExtensions = useAmiExtensions(open && subProject ? projectId : null, subProject?.id);
  const scope = {projectId: String(projectId), subProjectId: form.subProjectId};

  useEffect(() => {
    if (open) {
      setForm(toForm());
      numbers.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign?.id]);

  const setField = (field) => (e) => setForm(prev => ({...prev, [field]: e.target.value}));

  const {errors, isValid} = validateCampaignForm(form);
  const canSubmit = !saving && isValid && numbers.invalidCount === 0 && !!form.subProjectId;

  const create = async () => {
    const response = await CampaignRepository.create(buildCampaignBody(form, numbers.phoneNumbers, scope));
    toast.success("Campaign created");
    return response?.data;
  };

  // Existing numbers are left untouched (phoneNumbers: null); new ones are added separately
  const update = async () => {
    const response = await CampaignRepository.update(campaign.id, buildCampaignBody(form, null, scope));
    if (numbers.phoneNumbers.length > 0) {
      const added = await CampaignRepository.addPhoneNumbers(campaign.id, numbers.phoneNumbers.map(row => row.value.trim()));
      toast.info(addResultMessage(added?.data));
      const refreshed = await CampaignRepository.get(campaign.id);
      toast.success("Campaign updated");
      return refreshed?.data;
    }
    toast.success("Campaign updated");
    return response?.data;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const saved = isEdit ? await update() : await create();
      onSaved?.(saved);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || `Failed to ${isEdit ? "update" : "create"} campaign`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Fade in={open}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: {xs: "calc(100% - 24px)", md: "min(760px, 92vw)"}, maxHeight: "90vh",
          bgcolor: "#fff", borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)",
          display: "flex", flexDirection: "column", outline: "none",
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{p: 3, pb: 2}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Edit Campaign" : "Create Campaign"}
            </Typography>
            <IconButton size="small" onClick={saving ? undefined : onClose} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Box sx={{flex: 1, overflow: "auto", px: 3}}>
            <Grid container spacing={2}>
              <CampaignDetailsFieldsNew form={form} errors={errors} setField={setField}/>

              <Grid item xs={12}>
                <FieldLabel required>Sub project</FieldLabel>
                <Select
                  value={form.subProjectId}
                  // the extensions belong to the language's flow, so a new language needs a new extension
                  onChange={(e) => setForm(prev => ({...prev, subProjectId: e.target.value, extensionId: ""}))}
                  sx={{...selectSx, ...(subProject && !subProject.flowUid && {boxShadow: "inset 0 0 0 1.5px #DC2626"})}}
                  MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                >
                  {subProjects.map(item => (
                    <MenuItem key={item.id} value={String(item.id)}>{subProjectLabel(item)}</MenuItem>
                  ))}
                </Select>
                <FieldHelper error={!!subProject && !subProject.flowUid}>
                  {subProject && !subProject.flowUid
                    ? "This language has no flow yet - campaigns can't call through it"
                    : "Calls go through this language's voice bot and its SIP extensions"}
                </FieldHelper>
              </Grid>

              <CampaignCallFieldsNew form={form} setField={setField} amiExtensions={amiExtensions}/>
              <CampaignScheduleFieldsNew form={form} errors={errors} setField={setField}/>

              {isEdit && (
                <Grid item xs={12}>
                  <Box sx={{p: 1.5, borderRadius: "10px", bgcolor: "rgba(132, 145, 247, 0.08)"}}>
                    <Typography sx={{fontSize: 13, color: "#374151"}}>
                      The campaign's current {campaign.totalPhoneNumbers ?? 0} phone number(s) are kept. Numbers you
                      add here are added to them; numbers already in the campaign are skipped. Remove numbers from
                      the campaign page.
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <PhoneNumbersInputNew onAdd={numbers.addNumbers} label={isEdit ? "Add phone numbers" : "Phone numbers"}/>
              </Grid>
              <Grid item xs={12} sx={{pb: 2}}>
                <PhoneNumbersTableNew numbers={numbers}/>
              </Grid>
            </Grid>
          </Box>

          <Stack direction="row" gap={1.5} justifyContent="flex-end" sx={{p: 3, pt: 2}}>
            <Box
              component="button"
              onClick={saving ? undefined : onClose}
              disabled={saving}
              sx={{
                border: "none", cursor: "pointer", textTransform: "none", fontWeight: 600, fontSize: 13,
                borderRadius: "10px", color: "#374151", bgcolor: "#FAFAFD", px: 2, py: 1.1,
                "&:hover": {bgcolor: "#ECECF3"}, "&:disabled": {opacity: 0.5, cursor: "default"},
              }}
            >
              Cancel
            </Box>
            <Box
              component="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                border: "none", cursor: "pointer", textTransform: "none", fontWeight: 700, fontSize: 13,
                borderRadius: "10px", color: "#fff", bgcolor: "#8491F7", px: 2.5, py: 1.1,
                "&:hover": {bgcolor: "#6B78E8"}, "&:disabled": {bgcolor: "#D1D5DB", color: "#9CA3AF", cursor: "default"},
              }}
            >
              {saving ? "Saving..." : isEdit ? "Save" : "Create"}
            </Box>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  )
}
